import React, { useState } from 'react';
import { Formik, Field, Form } from 'formik';
import { Modal, Button, Alert, Form as BSForm, Row, Col, Toast, ToastContainer, InputGroup } from 'react-bootstrap';
import * as Yup from 'yup';
import axios from 'axios';

interface StaffToUser {
  FirstName: string;
  LastName: string;
  FirmEmail: string;
  Username: string;
  Administrator: string;
  JobTitle: string;
  Password: string;
  VerifyPassword: string;
  MustChange: string;
  IsLawyer: number;
}

interface StaffToNewUserProps {
  show: boolean;
  onHide: () => void;
  oldUser: any; // Replace 'any' with the actual type of your old user object
  SessionID: string;
  UserID: number;
  onUserAdded: (SubUserID: number) => void;
  userList: any[]; 
  onParentClose: () => void;
  prov: string; 
}

const StaffToNewUser: React.FC<StaffToNewUserProps> = ({
  show,
  onHide,
  oldUser,
  SessionID,
  UserID,
  onUserAdded,
  userList,
  onParentClose,
  prov
}) => {
  const [isUsernameAvailable, setIsUsernameAvailable] = useState<boolean>(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);

  const initialValues: StaffToUser = {
    FirstName: oldUser?.Name?.split(' ')[0] || '',
    LastName: oldUser?.Name?.split(' ').slice(1).join(' ') || '',
    FirmEmail: oldUser?.Email || '',
    Username: oldUser?.LDDUserName || '',
    Administrator: '0',
    JobTitle: '',
    Password: '',
    VerifyPassword: '',
    MustChange: '0',
    IsLawyer: 0
  };

  const UserTypes = [
    { label: 'Administrator', value: 1 },
    { label: 'Basic', value: 0 }
  ];

  const UserJobOptions = [
    { label: 'Lawyer', value: 'Lawyer' },
    { label: 'Clerk', value: 'Clerk' },
    { label: 'Conveyencer', value: 'Conveyencer' },
    { label: 'Paralegal', value: 'Paralegal' },
    { label: 'Notary', value: 'Notary' },
    { label: 'Accountant', value: 'Accountant' },
    { label: 'Other', value: 'Other' }
  ];

  const AddUserFormSchema = Yup.object().shape({
    FirstName: Yup.string()
      .matches(/^[a-zA-Z0-9\s-]+$/, {
        message: `Spaces and special characters are not allowed, for example: \\/:*?"<>|`,
      })
      .required('Please enter a First Name'),
    LastName: Yup.string()
      .matches(/^[a-zA-Z0-9\s-]+$/, {
        message: `Special characters are not allowed, for example: \\/:*?"<>|`,
      })
      .required('Please enter a Last Name'),
    FirmEmail: Yup.string()
      .email('Please enter a valid email address')
      .required('Please enter an email address'),
    Username: Yup.string()
      .matches(/^[a-zA-Z0-9]+$/, {
        message: `Spaces and special characters are not allowed, for example: \\/:*?"<>|`,
      })
      .required('Please enter a Username')
      .test('username-available', 'This username is already taken', function () {
        return isUsernameAvailable;
      }),
    Administrator: Yup.string()
      .required('Please select a User Type'),
    JobTitle: Yup.string()
      .required('Please select a Role'),
    Password: Yup.string()
      .required('Please enter a Password'),
    VerifyPassword: Yup.string()
      .required('Please verify your password')
      .oneOf([Yup.ref('Password')], 'Passwords must match'),
  });

  const checkUsernameAvailability = (username: string) => {
    const isAvailable = !userList.some(user => user.LDDUserName.toLowerCase() === username.toLowerCase());
    setIsUsernameAvailable(isAvailable);
  };

  const togglePasswordVisibility = (field: 'showPassword' | 'showVerifyPassword') => {
    if (field === 'showPassword') {
      setShowPassword(!showPassword);
    } else {
      setShowVerifyPassword(!showVerifyPassword);
    }
  };

  const addUser = async (SessionID: string, UserID: number, values: StaffToUser) => {
    try {
      const newUser: any = {
        SubUsername: values.Username,
        FirstName: values.FirstName,
        LastName: values.LastName,
        Name: values.FirstName + ' ' + values.LastName,
        Administrator: values.Administrator,
        JobTitle: values.JobTitle,
        FirmEmail: values.FirmEmail,
        Password: values.Password,
        MustChange: values.MustChange
      };

      const response = await axios.post(`/${process.env.REACT_APP_UVAPI_ENDPOINT}/api/${UserID}/uv/create/user?SessionID=${SessionID}`, newUser);
      if (response.data.result) {
        // After successfully creating the user, update the LDDUserName in the staff record
        try {
            const updateLDDUserName = await axios.post(
                `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${prov}/${UserID}/set/stafflddusername`,
                {
                    qpickrepository_id: oldUser.qpickrepository_id,
                    LDDUserName: values.Username
                },
                {
                    params: { SessionID }
                }
            );

            if (!updateLDDUserName.data.ok) {
                console.error('Error updating LDDUserName:', updateLDDUserName.data.error);
            }
          } catch (error) {
              console.error('Error updating LDDUserName:', error);
          }
      }

      return response.data.result;
  } catch (error) {
      console.error('Error adding user:', error);
      throw error;
  }
  };

  const handleSubmit = (values: StaffToUser) => {
    if (SessionID) {
      addUser(SessionID, UserID, values)
        .then((user) => {
          if (user.SubUserID) {
            onUserAdded(user.SubUserID);
            alert('User created successfully! The user can now log in with their credentials.');
            setShowToast(true);
            onHide();
            onParentClose();
          }
        })
        .catch((err) => {
          console.log(err);
          setToastMessage('Error creating user');
          setShowToast(true);
        });
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg">
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={AddUserFormSchema}
          enableReinitialize={true}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form autoComplete="off">
              <Modal.Header closeButton>
                <h2>Add New User</h2>
              </Modal.Header>
              <Modal.Body>
                <Alert variant="info">
                  All fields are required.
                </Alert>
                <Row className="mb-4">
                  <Col>
                    <BSForm.Label>First Name</BSForm.Label>
                    <Field
                      name="FirstName"
                      className="form-control"
                      placeholder="eg. Jane"
                    />
                    {errors.FirstName && touched.FirstName && (
                      <div className="input-error">{errors.FirstName}</div>
                    )}
                  </Col>
                  <Col>
                    <BSForm.Label>Last Name</BSForm.Label>
                    <Field
                      name="LastName"
                      className="form-control"
                      placeholder="eg. Smith"
                    />
                    {errors.LastName && touched.LastName && (
                      <div className="input-error">{errors.LastName}</div>
                    )}
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col>
                    <BSForm.Label>Username</BSForm.Label>
                    <Field name="Username">
                      {({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className="form-control"
                          placeholder="eg. jsmith"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            field.onChange(e);
                            checkUsernameAvailability(e.target.value);
                          }}
                        />
                      )}
                    </Field>
                    {errors.Username && touched.Username && (
                      <div className="input-error">{errors.Username}</div>
                    )}
                    {isUsernameAvailable && values.Username && (
                      <div className="input-success"></div>
                    )}
                  </Col>
                  <Col>
                    <BSForm.Label>Email</BSForm.Label>
                    <Field
                      name="FirmEmail"
                      className="form-control"
                      placeholder="eg. jane.smith@example.com"
                    />
                    {errors.FirmEmail && touched.FirmEmail && (
                      <div className="input-error">{errors.FirmEmail}</div>
                    )}
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col>
                    <BSForm.Label>User Type</BSForm.Label>
                    <Field
                      as="select"
                      name="Administrator"
                      className="form-select"
                    >
                      <option value="">- Select -</option>
                      {UserTypes.map(option => (
                        <option key={option.label} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Field>
                  </Col>
                  <Col>
                    <BSForm.Label>Role</BSForm.Label>
                    <Field
                      as="select"
                      name="JobTitle"
                      className="form-select"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        setFieldValue('JobTitle', e.target.value);
                        setFieldValue('IsLawyer', e.target.value === 'Lawyer' ? 1 : 0);
                      }}
                    >
                      <option value="">- Select -</option>
                      {UserJobOptions.map(option => (
                        <option key={option.label} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Field>
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col>
                    <BSForm.Label>Password</BSForm.Label>
                    <InputGroup>
                      <Field
                        type={showPassword ? "text" : "password"}
                        name="Password"
                        className="form-control"
                        placeholder="enter password.."
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue('Password', e.target.value);
                        }}
                      />
                      <InputGroup.Text 
                        onClick={() => togglePasswordVisibility('showPassword')}
                        style={{ cursor: 'pointer' }}
                      >
                        {showPassword ? <i className="fa-solid fa-eye-slash"></i> : <i className="fa-solid fa-eye"></i>}
                      </InputGroup.Text>
                    </InputGroup>
                    {errors.Password && touched.Password && (
                      <div className="input-error">{errors.Password}</div>
                    )}
                  </Col>
                  <Col>
                    <BSForm.Label>Verify Password</BSForm.Label>
                    <InputGroup>
                      <Field
                        type={showVerifyPassword ? "text" : "password"}
                        name="VerifyPassword"
                        className="form-control"
                        placeholder="enter password.."
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue('VerifyPassword', e.target.value);
                        }}
                      />
                      <InputGroup.Text 
                        onClick={() => togglePasswordVisibility('showVerifyPassword')}
                        style={{ cursor: 'pointer' }}
                      >
                        {showVerifyPassword ? <i className="fa-solid fa-eye-slash"></i> : <i className="fa-solid fa-eye"></i>}
                      </InputGroup.Text>
                    </InputGroup>
                    {errors.VerifyPassword && touched.VerifyPassword && (
                      <div className="input-error">{errors.VerifyPassword}</div>
                    )}
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col>
                    <BSForm.Label>Force Password Change</BSForm.Label>
                    <BSForm.Check
                      inline
                      type="checkbox"
                      id="mustChange"
                      name="MustChange"
                      onChange={(e) => setFieldValue("MustChange", e.target.checked ? '1' : '0')}
                      checked={values.MustChange === '1'}
                    />
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button className="cancel-button" variant="outline-primary" onClick={onHide}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!isUsernameAvailable || !values.FirstName || !values.LastName || !values.FirmEmail || !values.Username || !values.Administrator || !values.JobTitle || !values.Password || !values.VerifyPassword}
                >
                  Create User
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      <ToastContainer position="top-end" className="p-3" style={{ position: 'fixed', zIndex: 9999 }}>
        <Toast 
          onClose={() => setShowToast(false)} 
          show={showToast} 
          delay={3000} 
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Notification</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default StaffToNewUser;